import { useState } from 'react';
import {
  AtSign,
  Check,
  Copy,
  GitFork,
  MoreHorizontal,
  Pencil,
  RotateCcw,
  Trash2,
  FileText,
  Sparkles,
  BookmarkPlus,
  Library,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import type { Message, MessageAttachment } from '@/lib/workspace/types';
import { MarkdownContent } from './MarkdownContent';
import { MentionChips, NostrContextChips } from './NostrContextChips';
import { MessageReceipts } from './MessageReceipts';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

export type RewritePreset = 'shorter' | 'precise' | 'bullets';

export interface MessageActions {
  onEditSubmit?: (text: string) => void;
  onRegenerate?: () => void;
  onFork?: () => void;
  onDelete?: () => void;
  onContinue?: () => void;
  onRewrite?: (preset: RewritePreset) => void;
  /** Pin content into project memory (requires project). */
  onPinMemory?: () => void;
  /** Pin content into project context pack. */
  onPinContext?: () => void;
  /** Promote this message's Nostr chips into the project context pack. */
  onPinNostr?: () => void;
}

function AttachmentChips({ attachments }: { attachments: MessageAttachment[] }) {
  if (!attachments.length) return null;
  return (
    <div className="flex flex-wrap gap-2 mb-2">
      {attachments.map((a) =>
        a.kind === 'image' && a.data ? (
          <a
            key={a.id}
            href={a.data}
            target="_blank"
            rel="noopener noreferrer"
            className="block rounded-lg overflow-hidden border border-[#2a2a2a] max-w-[200px] max-h-[160px]"
          >
            <img
              src={a.data}
              alt={a.name || 'Attached image'}
              className="max-w-full max-h-[160px] object-contain bg-[#0f0f0f]"
            />
          </a>
        ) : (
          <div
            key={a.id}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-[#2a2a2a] bg-[#121212] text-[12px] text-[#8a8884] max-w-[220px]"
            title={a.name}
          >
            <FileText className="w-3.5 h-3.5 shrink-0" />
            <span className="truncate">{a.name || 'File'}</span>
          </div>
        ),
      )}
    </div>
  );
}

export function MessageLine({
  message,
  isStreaming = false,
  isLastAssistant = false,
  actions,
}: {
  message: Message;
  isStreaming?: boolean;
  isLastAssistant?: boolean;
  actions?: MessageActions;
}) {
  const isUser = message.role === 'user';
  const [copied, setCopied] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState(message.content);

  if (!isUser && !message.content && isStreaming) {
    return null;
  }

  if (
    !isUser &&
    !message.content &&
    !(message.attachments?.length) &&
    !(message.nostrContext?.length) &&
    !(message.mentions?.length)
  )
    return null;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(message.content);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      // ignore
    }
  };

  if (editing && isUser && actions?.onEditSubmit) {
    return (
      <div className="flex justify-end" data-message-id={message.id}>
        <div className="w-full max-w-[min(100%,42rem)] space-y-2">
          {message.attachments && message.attachments.length > 0 && (
            <AttachmentChips attachments={message.attachments} />
          )}
          {message.nostrContext && message.nostrContext.length > 0 && (
            <NostrContextChips chips={message.nostrContext} readOnly />
          )}
          {message.mentions &&
            message.mentions.length > 0 &&
            !(message.nostrContext?.some((c) => c.fromMention)) && (
              <MentionChips mentions={message.mentions} readOnly />
            )}
          <textarea
            value={editText}
            onChange={(e) => setEditText(e.target.value)}
            rows={4}
            className="w-full bg-[#1a1a1a] border border-[#3a3a3a] rounded-xl px-3 py-2.5 text-[15px] text-[#e8e6e3] resize-y focus:outline-none focus:border-[#5a5a5a] leading-relaxed"
            autoFocus
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                setEditing(false);
                setEditText(message.content);
              }
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                if (editText.trim() || message.attachments?.length) {
                  actions.onEditSubmit?.(editText);
                  setEditing(false);
                }
              }
            }}
          />
          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => {
                setEditing(false);
                setEditText(message.content);
              }}
              className="px-3 py-1.5 rounded-lg text-[12px] text-[#5a5a5a] hover:text-[#e8e6e3] hover:bg-[#1a1a1a]"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={!editText.trim() && !(message.attachments?.length)}
              onClick={() => {
                actions.onEditSubmit?.(editText);
                setEditing(false);
              }}
              className="px-3 py-1.5 rounded-lg text-[12px] bg-[#e8e6e3] text-[#0a0a0a] hover:bg-white disabled:opacity-30"
            >
              Save & regenerate
            </button>
          </div>
        </div>
      </div>
    );
  }

  const showActions = !isStreaming && !!actions;

  return (
    <div
      className={cn(
        'group relative',
        isUser ? 'flex justify-end' : 'flex justify-start',
      )}
      data-message-id={message.id}
    >
      <div
        className={cn(
          'relative min-w-0 max-w-[min(100%,48rem)]',
          isUser &&
            'bg-[#1a1a1a] border border-[#242424] rounded-2xl rounded-br-md px-4 py-3 shadow-sm',
        )}
      >
        {message.attachments && message.attachments.length > 0 && (
          <AttachmentChips attachments={message.attachments} />
        )}
        {message.nostrContext && message.nostrContext.length > 0 && (
          <div className="mb-2">
            <NostrContextChips chips={message.nostrContext} readOnly />
          </div>
        )}
        {/* Mentions without a matching chip (legacy / edge) still show avatar chips */}
        {message.mentions &&
          message.mentions.length > 0 &&
          !(message.nostrContext?.some((c) => c.fromMention)) && (
            <div className="mb-2">
              <MentionChips mentions={message.mentions} readOnly />
            </div>
          )}

        {isUser ? (
          message.content ? (
            <p className="text-[15px] text-[#e8e6e3] whitespace-pre-wrap break-words leading-relaxed">
              {message.content}
            </p>
          ) : null
        ) : (
          <MarkdownContent content={message.content} isStreaming={isStreaming} />
        )}

        {!isUser &&
          !isStreaming &&
          message.receipts &&
          message.receipts.length > 0 && (
            <MessageReceipts receipts={message.receipts} />
          )}

        {showActions && (
          <div
            className={cn(
              // Always faintly visible on touch; full on hover / focus
              'flex items-center gap-0.5 mt-2.5 transition-opacity',
              'opacity-40 sm:opacity-0 group-hover:opacity-100 focus-within:opacity-100',
              isLastAssistant && !isUser && 'sm:opacity-70',
              isUser ? 'justify-end' : 'justify-start',
            )}
          >
            <button
              type="button"
              onClick={() => void handleCopy()}
              className="p-1.5 rounded-lg text-[#5a5a5a] hover:text-[#e8e6e3] hover:bg-[#1a1a1a] transition-colors"
              title="Copy"
            >
              {copied ? (
                <Check className="w-3.5 h-3.5 text-green-500/80" />
              ) : (
                <Copy className="w-3.5 h-3.5" />
              )}
            </button>

            {isUser && actions.onEditSubmit && (
              <button
                type="button"
                onClick={() => {
                  setEditText(message.content);
                  setEditing(true);
                }}
                className="p-1.5 rounded-lg text-[#5a5a5a] hover:text-[#e8e6e3] hover:bg-[#1a1a1a] transition-colors"
                title="Edit & regenerate"
              >
                <Pencil className="w-3.5 h-3.5" />
              </button>
            )}

            {!isUser && isLastAssistant && actions.onRegenerate && (
              <button
                type="button"
                onClick={actions.onRegenerate}
                className="p-1.5 rounded-lg text-[#5a5a5a] hover:text-[#e8e6e3] hover:bg-[#1a1a1a] transition-colors"
                title="Regenerate"
              >
                <RotateCcw className="w-3.5 h-3.5" />
              </button>
            )}

            {!isUser && isLastAssistant && actions.onContinue && (
              <button
                type="button"
                onClick={actions.onContinue}
                className="p-1.5 rounded-lg text-[#5a5a5a] hover:text-[#e8e6e3] hover:bg-[#1a1a1a] transition-colors text-[11px] px-2 font-medium"
                title="Continue generating"
              >
                Continue
              </button>
            )}

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="p-1.5 rounded-lg text-[#5a5a5a] hover:text-[#e8e6e3] hover:bg-[#1a1a1a] transition-colors"
                  title="More"
                >
                  <MoreHorizontal className="w-3.5 h-3.5" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align={isUser ? 'end' : 'start'}
                className="min-w-[190px] bg-[#1a1a1a] border-[#2a2a2a] text-[#e8e6e3]"
              >
                <DropdownMenuItem
                  onClick={() => void handleCopy()}
                  className="text-[13px] focus:bg-[#242424] focus:text-[#e8e6e3]"
                >
                  <Copy className="w-3.5 h-3.5 mr-2" />
                  Copy markdown
                </DropdownMenuItem>
                {actions.onFork && (
                  <DropdownMenuItem
                    onClick={actions.onFork}
                    className="text-[13px] focus:bg-[#242424] focus:text-[#e8e6e3]"
                  >
                    <GitFork className="w-3.5 h-3.5 mr-2" />
                    Fork from here
                  </DropdownMenuItem>
                )}
                {(actions.onPinMemory || actions.onPinContext || actions.onPinNostr) && (
                  <>
                    <DropdownMenuSeparator className="bg-[#2a2a2a]" />
                    {actions.onPinMemory && (
                      <DropdownMenuItem
                        onClick={actions.onPinMemory}
                        className="text-[13px] focus:bg-[#242424] focus:text-[#e8e6e3]"
                      >
                        <BookmarkPlus className="w-3.5 h-3.5 mr-2" />
                        Pin to project memory
                      </DropdownMenuItem>
                    )}
                    {actions.onPinContext && (
                      <DropdownMenuItem
                        onClick={actions.onPinContext}
                        className="text-[13px] focus:bg-[#242424] focus:text-[#e8e6e3]"
                      >
                        <Library className="w-3.5 h-3.5 mr-2" />
                        Add to context pack
                      </DropdownMenuItem>
                    )}
                    {actions.onPinNostr && (
                      <DropdownMenuItem
                        onClick={actions.onPinNostr}
                        className="text-[13px] focus:bg-[#242424] focus:text-[#e8e6e3]"
                      >
                        <AtSign className="w-3.5 h-3.5 mr-2" />
                        Pin Nostr ref{(message.nostrContext?.length ?? 0) > 1 ? 's' : ''} to
                        project
                      </DropdownMenuItem>
                    )}
                  </>
                )}
                {!isUser && actions.onRewrite && (
                  <>
                    <DropdownMenuSeparator className="bg-[#2a2a2a]" />
                    <DropdownMenuItem
                      onClick={() => actions.onRewrite?.('shorter')}
                      className="text-[13px] focus:bg-[#242424] focus:text-[#e8e6e3]"
                    >
                      <Sparkles className="w-3.5 h-3.5 mr-2" />
                      Rewrite shorter
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => actions.onRewrite?.('precise')}
                      className="text-[13px] focus:bg-[#242424] focus:text-[#e8e6e3]"
                    >
                      <Sparkles className="w-3.5 h-3.5 mr-2" />
                      Rewrite more precise
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => actions.onRewrite?.('bullets')}
                      className="text-[13px] focus:bg-[#242424] focus:text-[#e8e6e3]"
                    >
                      <Sparkles className="w-3.5 h-3.5 mr-2" />
                      Rewrite as bullets
                    </DropdownMenuItem>
                  </>
                )}
                {actions.onDelete && (
                  <>
                    <DropdownMenuSeparator className="bg-[#2a2a2a]" />
                    <DropdownMenuItem
                      onClick={actions.onDelete}
                      className="text-[13px] text-red-400/90 focus:bg-red-950/40 focus:text-red-300"
                    >
                      <Trash2 className="w-3.5 h-3.5 mr-2" />
                      Delete
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}
      </div>
    </div>
  );
}
